/**
 * Adapter Wizard-Store → HeuristicsInput.
 *
 * Speist den Heuristik-Generator (`@tragfaehigkeit/core`) aus dem
 * lokalen Wizard-Store. Wenn die Wizard-Daten nicht ausreichen (kein
 * BWA-Datensatz im Resources-Slice), greifen wir für den Demo-Case auf
 * die `buildDemoStatementInput`-Fixture zurück, damit der Berater die
 * Heuristik im Demo-Modus testen kann.
 *
 * Pure Function — keine IO, keine Seiteneffekte (außer der bekannten
 * `console.warn` aus `selectBruttoForBgLuecke`).
 */

import type { statement, DimensionData, FinancialDataset, ScoreResult } from '@tragfaehigkeit/core';
import type { WizardCaseState } from '../store/wizard';
import { buildFinancialDatasetFromDraft, buildScoreResultFromCase } from '../store/scoreInput';
import { buildDemoStatementInput } from './demoFixture';

export interface HeuristicsAvailability {
  readonly canGenerate: boolean;
  /** Welche Datenquelle (echte Wizard-Daten oder Demo-Fixture)? */
  readonly source: 'wizard' | 'demo';
  /**
   * Sentinel-Hinweis, wenn Heuristik nicht möglich ist (z.B. anderer
   * Case ohne Wizard-Daten und kein Demo-Fallback).
   */
  readonly reason?: string;
}

/**
 * Liefert das `HeuristicsInput` für einen Case oder `null`, wenn keine
 * Datenbasis verfügbar ist.
 *
 * Strategie:
 *  - Wenn der Wizard genug Daten hat (mindestens BWA), verwende sie.
 *  - Sonst: für den Demo-Case nutze die Demo-Fixture (damit der Editor-
 *    UI-Test im /case/demo/cockpit-Pfad funktioniert).
 *  - Sonst: gebe einen klaren Reason zurück.
 */
export function buildHeuristicsInput(
  caseState: WizardCaseState | undefined,
): { input: statement.HeuristicsInput; source: 'wizard' | 'demo' } | null {
  // Pfad 1: Wizard hat eigene BWA-Daten?
  if (caseState) {
    const fd = buildFinancialDatasetFromDraft(caseState.resources, caseState.caseId);
    if (fd) {
      const score = buildScoreResultFromCase(caseState);
      const wizardInput = buildHeuristicsInputFromWizard(caseState, fd, score);
      return { input: wizardInput, source: 'wizard' };
    }
  }

  // Pfad 2: Demo-Fallback — nur für caseId === 'demo'
  if (caseState?.caseId === 'demo') {
    const demo = buildDemoStatementInput();
    return {
      input: {
        score: demo.score,
        financial: demo.financial,
        dimensions: demo.dimensions,
        business: {
          branch: demo.business.branch,
          legalForm: demo.business.legalForm,
          startDate: demo.business.startDate ?? new Date(),
        },
      },
      source: 'demo',
    };
  }

  return null;
}

function buildHeuristicsInputFromWizard(
  caseState: WizardCaseState,
  financial: FinancialDataset,
  score: ScoreResult,
): statement.HeuristicsInput {
  // Aktuell sind Dimensions-Daten im Wizard noch Stubs (Record<string,string>),
  // d.h. wir liefern keine `DimensionData[]` aus dem Wizard. Sobald die
  // strukturierten Dimensions-Schemas befüllt werden, ersetzt sich dieser
  // Block — hier reicht ein leeres Array.
  const dimensions: ReadonlyArray<DimensionData> = [];

  // Geschäftsdaten sind aktuell nicht im Wizard-Store, sondern werden
  // im Cockpit aus der DB geladen. Für die Heuristik nehmen wir die
  // Demo-Fallback-Werte als Default — sobald `business` in den Store
  // wandert, hier andocken.
  const demo = buildDemoStatementInput();
  void caseState; // reserviert für spätere Erweiterung

  return {
    score,
    financial,
    dimensions,
    business: {
      branch: demo.business.branch,
      legalForm: demo.business.legalForm,
      startDate: demo.business.startDate ?? new Date(),
    },
  };
}
